import React, { useEffect, useState } from 'react';
import { checkSession, getCurrentUser, logoutAll, exportSession } from '../api/sessionAPI';

/**
 * SessionBridge Component
 * 
 * This component creates endpoints that other applications can call
 * via postMessage API for cross-origin communication.
 * 
 * Usage from other apps (e.g., chat.deepsearchz.com):
 * 
 * // Check session
 * window.parent.postMessage({ type: 'CHECK_SESSION' }, 'https://deepsearchz.com');
 * 
 * // Get current user
 * window.parent.postMessage({ type: 'GET_USER' }, 'https://deepsearchz.com');
 * 
 * // Logout all
 * window.parent.postMessage({ type: 'LOGOUT_ALL' }, 'https://deepsearchz.com');
 * 
 * // Listen for response
 * window.addEventListener('message', (event) => {
 *   if (event.origin === 'https://deepsearchz.com') {
 *     console.log('Session data:', event.data);
 *   }
 * });
 */

export default function SessionBridge() {
  const [messageLog, setMessageLog] = useState([]);

  useEffect(() => {
    const handleMessage = async (event) => {
      // Security: Only accept messages from deepsearchz.com subdomains
      if (!event.origin.endsWith('deepsearchz.com')) {
        console.warn('[SessionBridge] Rejected message from unauthorized origin:', event.origin);
        return;
      }

      const { type, requestId } = event.data;

      console.log('[SessionBridge] Received message:', type, 'from', event.origin);

      setMessageLog(prev => [...prev, {
        timestamp: new Date().toISOString(),
        type,
        origin: event.origin,
        requestId
      }]);

      let response = {};

      try {
        switch (type) {
          case 'CHECK_SESSION':
            response = await checkSession();
            break;

          case 'GET_USER':
            response = await getCurrentUser();
            break;

          case 'LOGOUT_ALL':
            response = await logoutAll();
            break;

          case 'EXPORT_SESSION':
            response = exportSession();
            break;

          case 'PING':
            response = { success: true, message: 'pong', timestamp: new Date().toISOString() };
            break;

          default:
            response = { success: false, error: 'Unknown message type' };
        }

        // Send response back to the requesting origin
        event.source.postMessage(
          {
            type: `${type}_RESPONSE`,
            requestId,
            data: response,
            timestamp: new Date().toISOString()
          },
          event.origin
        );
      } catch (error) {
        console.error('[SessionBridge] Error handling message:', error);
        event.source.postMessage(
          {
            type: `${type}_RESPONSE`,
            requestId,
            error: error.message,
            timestamp: new Date().toISOString()
          },
          event.origin
        );
      }
    };

    window.addEventListener('message', handleMessage);

    // Announce that session bridge is ready
    window.parent.postMessage(
      { type: 'SESSION_BRIDGE_READY', timestamp: new Date().toISOString() },
      '*'
    );

    console.log('[SessionBridge] Session Bridge initialized and ready');

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  // This component doesn't render anything visible
  return null;
}